import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CircleNotch, LockKey } from "@phosphor-icons/react";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { api, formatApiErrorDetail } from "../../lib/api";

export default function AdminLogin({ onSuccess }: { onSuccess: () => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("fosche_admin_token", data.access_token);
      toast.success("Welcome back.");
      onSuccess();
    } catch (err: any) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#090A0C] flex items-center justify-center px-5 relative overflow-hidden" data-testid="admin-login-page">
      <div className="absolute inset-0 blueprint-grid opacity-40" style={{ ["--grid-line" as string]: "rgba(59,130,246,0.08)" }} />
      <div className="relative w-full max-w-sm border border-white/10 bg-[#121418] p-8">
        <Link to="/" className="flex items-center gap-3 mb-8">
          <img src="/brand/logo-icon.png" alt="FOSCHE logo" className="h-10 w-auto" />
          <span className="flex items-baseline gap-2">
            <span className="font-logo text-lg text-white">FOSCHE</span>
            <span className="font-mono-tech text-[10px] tracking-[0.25em] text-[#3B82F6]">ADMIN</span>
          </span>
        </Link>
        <p className="font-mono-tech text-[11px] tracking-[0.2em] uppercase text-white/40 mb-6 flex items-center gap-2">
          <LockKey size={14} /> Restricted Access
        </p>
        <form onSubmit={submit} className="space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="admin-email" className="text-white/70">Email</Label>
            <Input
              id="admin-email"
              data-testid="admin-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-none bg-[#090A0C] border-white/15 text-white"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="admin-password" className="text-white/70">Password</Label>
            <Input
              id="admin-password"
              data-testid="admin-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded-none bg-[#090A0C] border-white/15 text-white"
            />
          </div>
          <Button type="submit" data-testid="admin-login-button" disabled={submitting} className="w-full rounded-none bg-[#3B82F6] hover:bg-[#FFD60A] hover:text-black">
            {submitting ? <CircleNotch size={16} className="animate-spin" /> : "Sign In"}
          </Button>
        </form>
        <Link to="/" className="block mt-6 text-center text-xs text-white/40 hover:text-white/70 transition-colors">
          ← Back to website
        </Link>
      </div>
    </div>
  );
}
